import { type PosterTiling, posterTiling } from "./paginators.js";
import { PAPER_A4 } from "./paper.js";
import type { AssemblyInfo, PaperOrientation, PaperSize } from "./types.js";

/**
 * Sheet a tiled poster is printed on. A4 because that is what every home and
 * club printer takes — the whole point of the tiling switch.
 */
export const POSTER_PRINT_SHEET_ID = "A4";
export const POSTER_PRINT_SHEET: PaperSize = PAPER_A4;

/**
 * Width of the strip neighbouring sheets share, in millimetres.
 *
 * Sized against the unprintable border a normal printer leaves (~5 mm, worse
 * along the bottom edge on many inkjets), which is lost twice at every seam —
 * once per sheet. The requirement is
 * `overlap >= trim + printerMargin + glueArea`; at 20/8 a printer losing up to
 * 8 mm still leaves printed overlap to align on. Cutting these down is what
 * turns the seams into white stripes, so change them together and only with
 * that inequality in hand.
 */
export const POSTER_OVERLAP_MM = 20;
/** Millimetres trimmed off a sheet's left/top edge — also removes its own
 * white printer margin, which would otherwise show on the poster. */
export const POSTER_TRIM_MM = 8;

/**
 * Grid + scale for printing `posterPaper` across A4 sheets, or null when the
 * poster already fits one sheet (or the format is not DIN A).
 */
export function posterTilingOnPrintSheet(args: {
  board: { x: number; y: number; w: number; h: number };
  posterPaper: PaperSize;
  posterOrientation: PaperOrientation;
  overlapMm?: number;
}): PosterTiling | null {
  return posterTiling({
    board: args.board,
    posterPaper: args.posterPaper,
    posterOrientation: args.posterOrientation,
    printPaper: POSTER_PRINT_SHEET,
    overlapMm: args.overlapMm ?? POSTER_OVERLAP_MM,
  });
}

/** Assembly-sheet info for a tiled poster. */
export function boardPosterAssembly(
  tiling: PosterTiling,
  posterLabel: string,
  overlapMm: number = POSTER_OVERLAP_MM,
): AssemblyInfo {
  return {
    cols: tiling.cols,
    rows: tiling.rows,
    overlapMm,
    trimMm: POSTER_TRIM_MM,
    posterLabel,
    posterWMm: tiling.posterWMm,
    posterHMm: tiling.posterHMm,
  };
}
